import React, { useImperativeHandle, useRef, useState } from "react";
import { useScreen } from "../../core/Global";
import { BootstrapColors, FontAwesomeIcons } from "../../core/Types";
import HUBResponse from "../../models/HUBResponse";
import FlexRow from "../base/FlexRow";
import Form from "../form/Form";
import { Drawer, DrawerProps, DrawerRef } from "./Drawer";
import Icon from "../base/Icon";
import { Validator } from "../../core/Types";


export type DrawerFormRef = {
    close: () => void;
};

type DrawerFormProps = DrawerProps & {
    buttonIcon?: FontAwesomeIcons
    buttonText?: string
    /** Colour of the submit button. Primary unless the action deserves a warning, e.g. a cancellation. */
    buttonColor?: BootstrapColors
    cancelText?: string
    hideButtons?: boolean
    // Holds the submit button back while the form is not ready to be submitted -- payment
    // methods still loading, a required choice not yet made.
    confirmDisabled?: boolean
    // Checked before onSubmit by the console's form validation, which writes each message
    // beside the field's label.
    validator?: Validator[]
    onSubmit?: (body?: any, callback?: (res?: HUBResponse<any>) => void) => void
    onSubmitted?: () => void
}

export const DrawerForm = React.forwardRef<DrawerFormRef, DrawerFormProps>(function DrawerForm(props, ref) {

    const drawer = useRef<DrawerRef>(null)
    const screen = useScreen()

    const [opened, openedSet] = useState<boolean>(false)
    const [running, runningSet] = useState<boolean>(false)

    useImperativeHandle(ref, () => ({
        close: () => {
            drawer.current?.close()
        },
    }), []);


    const submit = async (body: any, callback?: (res?: HUBResponse<any>) => void) => {
        runningSet(true);

        if (props.onSubmit) {
            props.onSubmit(body, res => {
                runningSet(false);
                if (callback && res) callback(res);
                if (res?.success) {
                    if (props.onSubmitted) props.onSubmitted();
                    drawer.current?.close();
                }
            });
        }
    }

    return (
        <Drawer ref={drawer} {...props}
            onOpen={() => {
                openedSet(true)
                props.onOpen?.()
            }}
        >

            {
                opened &&
                <Form id={`${props.id}-form`}
                    validator={props.validator}

                    onSubmit={submit}>

                    {props.children}

                    {
                        !props.hideButtons &&
                        <>
                            {
                                screen.isMobile ?
                                    <div className="mt-8">

                                        {
                                            props.onSubmit &&
                                            <button id={props.id ? `${props.id}-submit` : undefined}
                                                className={`btn btn-sm btn-${props.buttonColor ?? "primary"} w-100 mb-2`}
                                                disabled={running || props.confirmDisabled}>
                                                {
                                                    running ?
                                                        <>
                                                            <Icon icon="spinner-third" spin />
                                                            <span className="ms-2">Please wait...</span>
                                                        </>
                                                        :
                                                        <>
                                                            {props.buttonText}
                                                        </>
                                                }

                                            </button>
                                        }

                                        <button type="button"
                                            className="btn btn-sm btn-secondary w-100"
                                            onClick={() => drawer.current?.close()}>
                                            {props.cancelText ?? "Discard"}
                                        </button>

                                    </div>
                                    :
                                    <FlexRow className="mt-8" column={screen.isMobile} justify="end">

                                        <button type="button"
                                            className="btn btn-sm btn-secondary w-125px me-2"
                                            onClick={() => drawer.current?.close()}>
                                            {props.cancelText ?? "Discard"}
                                        </button>

                                        {
                                            props.onSubmit &&
                                            <button id={props.id ? `${props.id}-submit` : undefined}
                                                className={`btn btn-sm btn-${props.buttonColor ?? "primary"} min-w-175px text-nowrap`}
                                                disabled={running || props.confirmDisabled}>
                                                {
                                                    running ?
                                                        <>
                                                            <Icon icon="spinner-third" spin />
                                                            <span className="ms-2">Please wait...</span>
                                                        </>
                                                        :
                                                        <>
                                                            {props.buttonText}
                                                        </>
                                                }

                                            </button>
                                        }

                                    </FlexRow>
                            }

                        </>
                    }


                </Form>
            }


        </Drawer>
    )
})